import { FC } from "react"

import { SEVERITY_BADGE } from "components/risk/constants"
import { scoreSeverity } from "components/risk/helpers"

import { FactorScore } from "services/risk/types"

const SeverityBadge: FC<{ severity: string }> = ({ severity }) => (
  <span className={`badge badge-xs ${SEVERITY_BADGE[severity] || "badge-ghost"}`}>{severity}</span>
)

interface Props {
  categoryName: string
  scores: Record<string, FactorScore | number>
  weightedScore: number
  categoryWeight?: number
  factorWeights?: Record<string, number>
  color: string
  defaultOpen?: boolean
}

const normalizeFactor = (value: FactorScore | number): FactorScore =>
  typeof value === "number" ? { score: value, weight: 0, severity: scoreSeverity(value) } : value

export const RiskFactorAccordion: FC<Props> = ({
  categoryName,
  scores,
  weightedScore,
  categoryWeight,
  factorWeights,
  color,
  defaultOpen
}) => {
  return (
    <div className="collapse collapse-arrow bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-xl">
      <input type="checkbox" defaultChecked={defaultOpen} />
      <div className="collapse-title">
        <div className="flex justify-between items-center">
          <span className="font-semibold">
            {categoryName}
            {categoryWeight !== undefined && (
              <span className="text-xs font-normal ml-2 text-gray-400 dark:text-gray-500">{categoryWeight}%</span>
            )}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Weighted: <span className={`text-${color} font-semibold`}>{weightedScore.toFixed(1)}</span>
          </span>
        </div>
      </div>
      <div className="collapse-content">
        {Object.entries(scores).map(([key, raw]) => {
          const factor = normalizeFactor(raw)
          return (
            <div
              key={key}
              className="flex items-center justify-between py-3 border-b last:border-0 border-gray-100 dark:border-[rgba(255,255,255,0.06)]"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm capitalize">{key.replace(/_/g, " ")}</span>
                <SeverityBadge severity={factor.severity || scoreSeverity(factor.score)} />
                {factorWeights?.[key] !== undefined && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">{factorWeights[key]}% weight</span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <progress className={`progress progress-${color} w-24`} value={factor.score} max={100} />
                <span className="text-sm font-mono w-16 text-right">{factor.score.toFixed(0)} / 100</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
